import React from 'react';
import { Badge } from 'subframe';

import {
  Table,
  TableBody,
  TableCol,
  TableHead,
  TableHeader,
  TableRow,
} from 'common/components/Table';
import { Candidate, CandidateListFilter } from 'modules/Recruit/types';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'subframe/components/Alert';
import { getTimeAgo } from 'utils/date';
import { formatLocalDate } from 'utils/helper';
import { getCandidateProfileRoute } from '../../../constants/routes';
import { CandidateListTableSkeleton } from './CandidateListTableSkeleton';

interface CandidateListTableProps {
  rows: Candidate[];
  filters?: CandidateListFilter;
  loading?: boolean;
}

export const CandidateListTable: React.FC<CandidateListTableProps> = ({
  rows,
  loading = false,
}) => {
  const navigate = useNavigate();

  const handleCandidateDetails = (candidateId: number) => {
    const profileRoute = getCandidateProfileRoute(candidateId);
    navigate(profileRoute);
  };

  const handleRowClick = (row: Candidate) => {
    navigate(getCandidateProfileRoute(row.candidate_id));
  };

  const tableRows = rows.map((row, index) => ({
    ...row,
    key: `${row.candidate_id}-${index}`,
    time_in_stage: row.time_in_stage,
    date_applied: getTimeAgo(row.date_applied),
  }));

  if (loading) {
    return <CandidateListTableSkeleton />;
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="w-full">
        <Alert variant="neutral" title="No candidates found">
          No candidates match the current filters. Try adjusting your search criteria.
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <Table className="table-fixed w-full">
        <TableHead>
          <TableRow isHeader>
            <TableHeader className="w-auto">First Name</TableHeader>
            <TableHeader className="w-auto">Last Name</TableHeader>
            <TableHeader className="w-auto">Phone Number</TableHeader>
            <TableHeader className="w-auto">Region</TableHeader>
            <TableHeader className="w-auto">Location</TableHeader>
            <TableHeader className="w-auto">Stage</TableHeader>
            <TableHeader className="w-auto">Entered Stage At</TableHeader>
            <TableHeader className="w-auto">Time in Stage</TableHeader>
            <TableHeader className="w-auto">Date Applied</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map(row => (
            <TableRow
              key={row.key}
              onClick={() => handleRowClick(row)}
              className="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
            >
              <TableCol>
                <div className="flex flex-col">
                  <span className="text-body-bold font-body-bold text-default-font truncate">
                    {row.first_name || '-'}
                  </span>
                </div>
              </TableCol>

              <TableCol className="!pl-0">
                <div className="flex flex-col">
                  <span className="text-body-bold font-body-bold text-default-font truncate">
                    {row.last_name || '-'}
                  </span>
                </div>
              </TableCol>

              <TableCol className="md:table-cell">
                <span className="text-body font-body text-default-font truncate">
                  {row.phone_number}
                </span>
              </TableCol>

              <TableCol className="lg:table-cell">
                <span className="text-body font-body text-default-font truncate">
                  {row.region || '-'}
                </span>
              </TableCol>

              <TableCol className="lg:table-cell">
                <span className="text-body font-body text-default-font truncate">
                  {row.location || '-'}
                </span>
              </TableCol>

              <TableCol>
                <div className="w-fit">
                  <Badge
                    variant={row.is_flow_completed ? 'success' : 'neutral'}
                    className="text-xs font-medium"
                  >
                    <span className="hidden sm:inline">{row.stage}</span>
                    <span className="sm:hidden">
                      {row.stage.length > 8 ? row.stage.substring(0, 8) + '...' : row.stage}
                    </span>
                  </Badge>
                </div>
              </TableCol>

              <TableCol className="sm:table-cell">
                <span className="text-body font-body text-default-font text-xs sm:text-sm truncate">
                  {row.entered_stage_at ? formatLocalDate(row.entered_stage_at, true) : '-'}
                </span>
              </TableCol>

              <TableCol className="md:table-cell">
                <span className="text-body font-body text-default-font truncate">
                  {row.time_in_stage || '-'}
                </span>
              </TableCol>

              <TableCol className="sm:table-cell">
                <span className="text-body font-body text-default-font text-xs sm:text-sm truncate">
                  {row.date_applied}
                </span>
              </TableCol>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
